import { Link } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { formatDistanceToNow, format } from 'date-fns';
import { ArrowLeft, Feather, BookOpen } from 'lucide-react';
import { nip19 } from 'nostr-tools';
import { Layout } from '@/components/Layout';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuthor } from '@/hooks/useAuthor';
import { useWisdomLetter, WISDOM_KIND } from '@/hooks/useWisdomLetters';
import NotFound from './NotFound';

interface LetterPageProps {
  eventId: string;
}

const TOPIC_COLORS: Record<string, string> = {
  relationships: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
  career: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  health: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  money: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
  family: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  mindset: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  education: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
  travel: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
};

function getTopicClass(topic: string): string {
  return TOPIC_COLORS[topic.toLowerCase()] ?? 'bg-accent text-accent-foreground';
}

function AuthorRow({ pubkey }: { pubkey: string }) {
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;
  const displayName = metadata?.name ?? `${pubkey.slice(0, 8)}…`;
  const avatar = metadata?.picture;
  const initials = displayName.slice(0, 2).toUpperCase();
  const npub = nip19.npubEncode(pubkey);

  if (author.isLoading) {
    return (
      <div className="flex items-center gap-3">
        <Skeleton className="w-10 h-10 rounded-full" />
        <div className="space-y-1.5">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-20" />
        </div>
      </div>
    );
  }

  return (
    <a href={`https://primal.net/p/${npub}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 group w-fit">
      <Avatar className="w-10 h-10">
        <AvatarImage src={avatar} alt={displayName} />
        <AvatarFallback className="bg-primary/10 text-primary font-semibold">
          {initials}
        </AvatarFallback>
      </Avatar>
      <div>
        <p className="font-medium text-foreground group-hover:text-primary transition-colors">
          {displayName}
        </p>
        {metadata?.nip05 && (
          <p className="text-xs text-muted-foreground">{metadata.nip05}</p>
        )}
      </div>
    </a>
  );
}

export default function LetterPage({ eventId }: LetterPageProps) {
  const { data: letter, isLoading, isError } = useWisdomLetter(eventId);

  useSeoMeta({
    title: letter ? `${letter.title} — Hindsight Wisdom` : 'Hindsight Wisdom',
    description: letter ? letter.body.slice(0, 160) : undefined,
  });

  if (isLoading) {
    return (
      <Layout>
        <div className="container py-10 max-w-2xl mx-auto">
          <Skeleton className="h-4 w-24 mb-8" />
          <div className="space-y-4">
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-5 w-1/2" />
            <Skeleton className="h-4 w-40 mt-6" />
          </div>
          <div className="mt-10 space-y-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className={`h-4 ${i % 4 === 3 ? 'w-3/4' : 'w-full'}`} />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  if (isError || !letter) {
    return <NotFound />;
  }

  // Verify this is actually a wisdom letter
  if (letter.event.kind !== WISDOM_KIND) {
    return <NotFound />;
  }

  const publishedDate = new Date(letter.event.created_at * 1000);
  const timeAgo = formatDistanceToNow(publishedDate, { addSuffix: true });
  const fullDate = format(publishedDate, 'MMMM d, yyyy');

  // Render body paragraphs
  const paragraphs = letter.body.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);

  return (
    <Layout>
      <article className="container py-10 max-w-2xl mx-auto">
        {/* Back */}
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to all letters
        </Link>

        {/* Topics */}
        {letter.topics.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-5">
            {letter.topics.map((topic) => (
              <span
                key={topic}
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTopicClass(topic)}`}
              >
                {topic}
              </span>
            ))}
          </div>
        )}

        {/* Title + subject */}
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-foreground leading-tight mb-3">
          {letter.title}
        </h1>
        <p className="font-serif text-lg text-muted-foreground italic mb-8">
          To: {letter.subject}
        </p>

        {/* Author + date */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-8 mb-8 border-b border-border">
          <AuthorRow pubkey={letter.event.pubkey} />
          <time
            dateTime={publishedDate.toISOString()}
            title={fullDate}
            className="text-sm text-muted-foreground"
          >
            {timeAgo}
          </time>
        </div>

        {/* Letter body */}
        <div className="letter-body">
          {paragraphs.map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>

        {/* Signature decoration */}
        <div className="mt-12 pt-8 border-t border-border flex items-center gap-3 text-muted-foreground">
          <BookOpen className="w-4 h-4 shrink-0" />
          <p className="text-sm font-serif italic">
            Written with hindsight, shared with love.
          </p>
        </div>

        {/* Share / back CTA */}
        <div className="mt-10 p-6 rounded-xl bg-accent/30 border border-border text-center">
          <Feather className="w-8 h-8 text-primary/40 mx-auto mb-3" />
          <p className="font-serif text-base font-medium mb-1">What would <em>you</em> tell your younger self?</p>
          <p className="text-sm text-muted-foreground mb-4">Your story could help someone else.</p>
          <Link to="/write">
            <button className="inline-flex items-center gap-2 px-6 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors">
              <Feather className="w-3.5 h-3.5" />
              Write your letter
            </button>
          </Link>
        </div>
      </article>
    </Layout>
  );
}
